/**
 * Barcode-scanner burst detection for text inputs.
 *
 * A hardware scanner behaves like a keyboard: it "types" the code as a rapid
 * run of characters (typically 10–30 ms apart) and terminates with Enter (or
 * Tab, depending on configuration). People don't sustain gaps that short, so a
 * run of ≥ `minLength` characters with every gap under `maxGapMs`, ending in a
 * terminator, is treated as a scan.
 *
 * Tracking the burst separately from the field's value is what lets a scan
 * REPLACE whatever was in the box rather than append to it: when the
 * terminator arrives, the caller is handed just the scanned characters and
 * sets the field to those. Anything typed by hand before the scan (or a
 * previous scan's code) never becomes part of the new search term.
 *
 * Pure and framework-free so the timing rules are unit-testable; the React
 * hook in hooks/use-barcode-scan.ts wraps it.
 */
export interface ScanDetectorOptions {
  /** Shortest run that counts as a scan. Defaults to 3. */
  minLength?: number;
  /** Longest gap (ms) between two keystrokes of one scan. Defaults to 50. */
  maxGapMs?: number;
}

export interface ScanDetector {
  /**
   * Feed one keydown. Returns the scanned code when `key` terminates a
   * qualifying burst, otherwise null. `now` is the event timestamp in ms.
   */
  keydown(key: string, now: number): string | null;
  /** Forget any partial burst (e.g. when the field is blurred or cleared). */
  reset(): void;
}

const TERMINATORS = new Set(['Enter', 'Tab']);

export function createScanDetector({ minLength = 3, maxGapMs = 50 }: ScanDetectorOptions = {}): ScanDetector {
  let burst = '';
  let lastAt = Number.NEGATIVE_INFINITY;

  return {
    keydown(key, now) {
      if (TERMINATORS.has(key)) {
        // Only a terminator that arrives as fast as the characters did belongs
        // to the burst; a hand-typed Enter after a pause is an ordinary Enter.
        const code = burst.length >= minLength && now - lastAt <= maxGapMs ? burst : null;
        burst = '';
        lastAt = Number.NEGATIVE_INFINITY;
        return code;
      }
      // Only printable single characters are part of a code — modifiers,
      // arrows, Backspace etc. break the run.
      if (key.length !== 1) {
        burst = '';
        lastAt = Number.NEGATIVE_INFINITY;
        return null;
      }
      burst = now - lastAt <= maxGapMs ? burst + key : key;
      lastAt = now;
      return null;
    },
    reset() {
      burst = '';
      lastAt = Number.NEGATIVE_INFINITY;
    },
  };
}
